import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import { api } from '@/lib/api-client';

export interface EditorBundleResponse {
    root: Record<string, unknown>;
    localRefs: Record<string, Record<string, unknown>>;
    crossRegistryRefs: CrossRegistryRefStub[];
    etags: Record<string, string>;
}

export interface CrossRegistryRefStub {
    refCapId: string;
    authorityRegistryId: string;
    authorityAlias?: string;
    etag?: string;
    cardSummary?: {
        title: string;
        type: string;
        latestVersion: string;
    };
}

const BUNDLE_STALE_TIME = 30_000;

/**
 * Fetches a capability and its referenced capabilities in a single request.
 * Uses ETag-based caching — the server returns 304 when the bundle hasn't changed.
 * Falls back to useCapabilityDetail behavior when the bundle endpoint isn't available.
 */
export function useEditorBundle(appKey: string, name: string, opts?: { depth?: number }) {
    const etagRef = useRef<string | null>(null);
    const queryClient = useQueryClient();

    const query = useQuery<EditorBundleResponse>({
        queryKey: ['editor-bundle', appKey, name, opts?.depth],
        queryFn: async () => {
            const params: Record<string, unknown> = {};
            if (opts?.depth) params.depth = opts.depth;

            const headers: Record<string, string> = {};
            if (etagRef.current) {
                headers['If-None-Match'] = etagRef.current;
            } else {
                params._t = Date.now();
            }

            const response = await api.get(
                `/applications/${appKey}/capabilities/${name}/bundle`,
                {
                    params,
                    headers,
                    validateStatus: (status: number) => status === 200 || status === 304,
                },
            );

            if (response.status === 304) {
                const cached = queryClient.getQueryData<EditorBundleResponse>([
                    'editor-bundle', appKey, name, opts?.depth,
                ]);
                if (cached) return cached;
            }

            const etag = response.headers?.etag;
            if (etag) {
                etagRef.current = etag;
            }

            return response.data as EditorBundleResponse;
        },
        enabled: !!appKey && !!name,
        staleTime: BUNDLE_STALE_TIME,
    });

    /** Refetch and bypass ETag cache (use after saving changes) */
    const refetchWithoutCache = useCallback(() => {
        etagRef.current = null;
        return queryClient.fetchQuery({
            queryKey: ['editor-bundle', appKey, name, opts?.depth],
            queryFn: async () => {
                const response = await api.get(
                    `/applications/${appKey}/capabilities/${name}/bundle`,
                    {
                        params: opts?.depth ? { depth: opts.depth } : undefined,
                        headers: { 'Cache-Control': 'no-cache' },
                        validateStatus: (status: number) => status === 200,
                    },
                );
                const etag = response.headers?.etag;
                if (etag) etagRef.current = etag;
                return response.data as EditorBundleResponse;
            },
        });
    }, [appKey, name, opts?.depth, queryClient]);

    return { ...query, refetchWithoutCache };
}

/**
 * Convenience: extract the root entity from a bundle response in the same
 * shape that useCapabilityDetail returns, so existing editor code can use
 * it as a drop-in replacement.
 */
export function useBundleRoot(bundle: EditorBundleResponse | undefined) {
    return bundle?.root ?? null;
}

/**
 * Look up a referenced capability from the bundle's localRefs by its FQCN.
 */
export function useBundleRef(
    bundle: EditorBundleResponse | undefined,
    refCapId: string,
) {
    return bundle?.localRefs?.[refCapId] ?? null;
}
